"use client";

import { useEffect, useId, useRef } from "react";

/**
 * Faz o botão/gesto de voltar (Android) fechar um modal ou folha aberta em vez de sair do app.
 * Ao abrir empilha uma entrada no histórico; ao voltar, chama onClose; se o modal fechar por
 * outro caminho (X, Esc, salvar), a entrada que sobrou é desempilhada.
 */
export function useBackToClose(open: boolean | undefined, onClose: () => void) {
  const id = useId();
  const onCloseRef = useRef(onClose);
  const openRef = useRef(false);
  const closedByPop = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    openRef.current = Boolean(open);
    if (!open || typeof window === "undefined") return;
    closedByPop.current = false;

    const state = window.history.state;
    if (state?.__backClose !== id) {
      window.history.pushState({ ...(state ?? {}), __backClose: id }, "");
    }

    const onPop = () => {
      // Ainda é a minha entrada (voltou de um modal aberto por cima): continua aberto.
      if (window.history.state?.__backClose === id) return;
      closedByPop.current = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
      if (closedByPop.current) return;
      // Adiado: no StrictMode o efeito roda de novo logo em seguida e o modal continua aberto.
      setTimeout(() => {
        if (!openRef.current && window.history.state?.__backClose === id) window.history.back();
      }, 0);
    };
  }, [open, id]);
}
